import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
} from 'recharts';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../../Utils/toastContext';
import Dropdown from '../../Components/CustomDropdown/customdropdown';

const OngoingMonitoringModule = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [riskProfile, setRiskProfile] = useState(null);
  const [observedBehaviour, setObservedBehaviour] = useState(null);
  const [graphData, setGraphData] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [heatMapData, setHeatMapData] = useState([]);

  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [preSelectedFilter, setpreSelectedFilter] = useState({
    value: 10,
    label: '10',
  });
  const [drift, setDrift] = useState(null);
  const filterOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 30, label: '30' },
    { value: 50, label: '50' },
  ];
  const toTitleCase = (str) =>
    str
      ?.replace(/_/g, ' ')
      ?.toLowerCase()
      ?.replace(/\b\w/g, (c) => c.toUpperCase());
  const monitoringTrendData = [
    { date: 'Mon', declaredRisk: 20, actualRisk: 30 },
    { date: 'Tue', declaredRisk: 25, actualRisk: 45 },
    { date: 'Wed', declaredRisk: 30, actualRisk: 50 },
    { date: 'Thu', declaredRisk: 28, actualRisk: 60 },
    { date: 'Fri', declaredRisk: 35, actualRisk: 70 },
    { date: 'Sat', declaredRisk: 40, actualRisk: 75 },
    { date: 'Sun', declaredRisk: 38, actualRisk: 78 },
  ];
  const chartData = graphData?.map((item) => ({
    date: item.date,
    low: item.data?.low || 0,
    medium: item.data?.medium || 0,
    high: item.data?.high || 0,
  }));
  const handleFilterChange = (selectedOption) => {
    console.log('Selected Option:', selectedOption);
    setpreSelectedFilter(selectedOption);
    setLimit(parseInt(selectedOption.value));
    setCurrentPage(1);
  };
  const getRiskStyle = (risk) => {
    switch (risk) {
      case 'low':
        return 'bg-emerald-50 text-emerald-700 border-emerald-200';
      case 'medium':
        return 'bg-amber-50 text-amber-700 border-amber-200';
      case 'high':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-slate-50 text-slate-500 border-slate-200';
    }
  };
  // helper for color mapping
  const getRiskColor = (risk) => {
    switch (risk) {
      case 'high':
        return 'bg-red-500 hover:bg-red-600';
      case 'medium':
        return 'bg-amber-400 hover:bg-amber-500';
      case 'low':
        return 'bg-emerald-400 hover:bg-emerald-500';
      default:
        return 'bg-gray-200'; // null / no data
    }
  };

  // ensure fixed grid size (72 cells)
  const heatmapDataCheck = [...(heatMapData || [])];

  // pad empty cells
  while (heatmapDataCheck.length < 72) {
    heatmapDataCheck.push({
      date: null,
      customer: null,
      risk_status: null,
    });
  }
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }
      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const getRiskAssessmentSummary = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/agency/dashboard/risk-assessment`,
      );

      if (response.status == 200) {
        setRiskProfile(response.data?.agency_risk_assessment);
        setObservedBehaviour(response.data?.agency_observed_behavior);
        setDrift(response.data.agency_observed_behavior?.observed);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          showToast(response.message, 'error');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const getRiskAssessmentGraph = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/agency/dashboard/risk-assessment/graph`,
      );

      if (response.status == 200) {
        setGraphData(response.data);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          showToast(response.message, 'error');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const getHeatMapData = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/agency/dashboard/transaction-activity-heatmap`,
      );

      if (response.status == 200) {
        setHeatMapData(response.data);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          showToast(response.message, 'error');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const getTransactions = async () => {
    try {
      const response = await GetAPIcalls(
        `/api/v1/agency/customers/recent-transactions?page=${currentPage}&limit=${limit}`,
      );

      if (response.status == 200) {
        setTableData(response.data.items);
        setCurrentPage(response.data.page);
        setTotalPages(response.data.total_pages);
        setTotalCount(response.data.total);
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
        if (response.status === 400) {
          showToast(response.message, 'error');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  useEffect(() => {
    getTransactions();
  }, [currentPage, limit]);
  useEffect(() => {
    getRiskAssessmentSummary();
    getRiskAssessmentGraph();
    getHeatMapData();
  }, []);
  return (
    <div className="pt-6 lg:min-w-[79vw] lg:max-w-[80vw] space-y-6 bg-gradient-to-br from-gray-50 via-white to-gray-100 p-4 rounded-2xl">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-3">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            🔎 Continuous Risk Intelligence
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Live comparison of declared risk vs actual behavioural patterns
          </p>
        </div>

        {/* STATUS PILLS */}
        {/* <div className="flex flex-wrap gap-2">
      <span className="px-3 py-1 text-xs rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100">
        ● System Active
      </span>
      <span className="px-3 py-1 text-xs rounded-full bg-amber-50 text-amber-700 border border-amber-100">
        ⚠ 12 Active Alerts
      </span>
      <span className="px-3 py-1 text-xs rounded-full bg-red-50 text-red-700 border border-red-100">
        ⛔ 3 High Risk Cases
      </span>
    </div> */}
      </div>

      {/* KPI CARDS */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="bg-white rounded-2xl p-5 shadow-md border hover:shadow-lg transition">
          <p className="text-xs text-gray-500">Declared Risk Profile</p>
          <h2
            className={`${riskProfile == 'low' ? 'text-emerald-600' : riskProfile == 'high' ? 'text-red-600' : 'text-amber-600'} text-2xl font-bold  mt-1`}
          >
            {riskProfile?.charAt(0).toUpperCase() + riskProfile?.slice(1)}
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            Based on risk assessment questionnaire
          </p>
        </div>
        <div className="bg-white rounded-2xl p-5 shadow-md border hover:shadow-lg transition w-full">
          {/* HEADER */}
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide">
                Business Observed Behaviour
              </p>
              <h2 className="text-sm font-semibold text-slate-800 mt-1">
                Risk Distribution Overview
              </h2>
            </div>
          </div>

          {/* COUNTERS */}
          <div className="grid grid-cols-3 gap-3 mt-4">
            {/* LOW */}
            <div className="rounded-xl p-3 bg-emerald-50 border border-emerald-100 text-center">
              <p className="text-[11px] text-emerald-600 font-medium">Low</p>
              <p className="text-lg font-bold text-emerald-700">
                {observedBehaviour?.low ?? 0}
              </p>
            </div>

            {/* MEDIUM */}
            <div className="rounded-xl p-3 bg-amber-50 border border-amber-100 text-center">
              <p className="text-[11px] text-amber-600 font-medium">Medium</p>
              <p className="text-lg font-bold text-amber-700">
                {observedBehaviour?.medium ?? 0}
              </p>
            </div>

            {/* HIGH */}
            <div className="rounded-xl p-3 bg-red-50 border border-red-100 text-center">
              <p className="text-[11px] text-red-600 font-medium">High</p>
              <p className="text-lg font-bold text-red-700">
                {observedBehaviour?.high ?? 0}
              </p>
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden bg-white rounded-2xl p-5 shadow-md border hover:shadow-lg transition">
          {/* subtle background glow */}

          <div className="relative flex items-start justify-between">
            {/* LEFT CONTENT */}
            <div>
              <p className="text-[11px] uppercase tracking-wide text-slate-500 flex items-center gap-1">
                Risk Drift Indicator
              </p>

              <h2 className="text-xl font-bold text-slate-900 mt-1">
                {toTitleCase(drift)}
              </h2>

              <p className="text-xs text-slate-500 mt-1">
                Escalation detected vs baseline profile
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* TREND */}
      <div className="bg-white p-5 rounded-2xl border shadow-sm hover:shadow-md transition">
        <h3 className="text-sm font-semibold text-gray-800 mb-3">
          📊 Behavioural Risk Trajectory
        </h3>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={chartData}>
            <XAxis tick={{ fontSize: 11, fill: '#6b7280' }} dataKey="date" />
            <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} />
            <Tooltip />

            {/* LOW */}
            <Line
              type="monotone"
              dataKey="low"
              stroke="#22c55e"
              strokeWidth={2.5}
              name="Low Risk"
            />

            {/* MEDIUM */}
            <Line
              type="monotone"
              dataKey="medium"
              stroke="#f59e0b"
              strokeWidth={2.5}
              name="Medium Risk"
            />

            {/* HIGH */}
            <Line
              type="monotone"
              dataKey="high"
              stroke="#ef4444"
              strokeWidth={2.5}
              name="High Risk"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="bg-white rounded-2xl shadow-md border p-4">
        {/* HEADER */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 mb-4">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">
              Recent Transactions
            </h2>
            <p className="text-xs text-slate-500">
              Risk monitored transaction activity
            </p>
          </div>

          {/* PAGE SIZE SELECTOR */}
          <div className="flex items-center gap-2 text-sm">
            <span className="text-slate-500">Show Records</span>

            <Dropdown
              label=""
              options={filterOptions}
              preselectedValue={preSelectedFilter}
              onSelect={handleFilterChange}
            />
          </div>
        </div>

        {/* TABLE */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-slate-500 border-b">
                <th className="py-2">Team Member</th>
                <th>Customer</th>
                <th>Risk Status</th>
                <th>Date</th>
              </tr>
            </thead>

            <tbody>
              {tableData.map((item, idx) => (
                <tr
                  key={idx}
                  className="border-b last:border-0 hover:bg-slate-50 transition"
                >
                  {/* AGENT */}
                  <td className="py-3 font-medium text-slate-700">
                    {item.agent?.name}
                  </td>

                  {/* CUSTOMER */}
                  <td className="text-slate-600">{item.customer?.name}</td>

                  {/* RISK */}
                  <td>
                    <span
                      className={`px-3 py-1 rounded-full border text-xs font-medium ${getRiskStyle(
                        item.risk_status,
                      )}`}
                    >
                      {item.risk_status
                        ? item.risk_status.toUpperCase()
                        : 'NOT SCORED'}
                    </span>
                  </td>

                  {/* DATE */}
                  <td className="text-slate-500">{item.created_at}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* PAGINATION */}
        <div className="flex items-center justify-between mt-4 text-sm">
          <div className="text-[#8C8C8C] text-sm sm:text-base w-full sm:w-auto text-center sm:text-left">
            Showing {limit * (currentPage - 1) + 1} to{' '}
            {currentPage === totalPages || totalPages == 0
              ? totalCount
              : Math.max(limit * currentPage, tableData.length)}{' '}
            of {totalCount} entries
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              className="px-3 py-1 rounded-lg border disabled:opacity-50"
            >
              Prev
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }).map((_, index) => {
              const startPage = Math.max(
                1,
                Math.min(totalPages - 4, currentPage - 2),
              );
              const page = startPage + index;

              return (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded ${
                    currentPage === page
                      ? 'bg-[#375AE7] text-white'
                      : 'bg-white border border-gray-300 text-black'
                  }`}
                >
                  {page}
                </button>
              );
            })}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              className="px-3 py-1 rounded-lg border disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
      {/* ALERT TABLE */}
      {/* <div className="bg-white p-5 rounded-2xl border shadow-sm hover:shadow-md transition">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">
          🚨 Active Monitoring Alerts
        </h3>

        <table className="w-full text-xs">
          <thead className="text-gray-500 border-b">
            <tr>
              <th className="text-left p-2">Customer</th>
              <th className="text-left p-2">Trigger Event</th>
              <th className="text-left p-2">Risk Level</th>
              <th className="text-left p-2">Case Status</th>
            </tr>
          </thead>

          <tbody>
            {[
              {
                name: "William Brown",
                trigger: "Large cash deposit",
                risk: "High",
                status: "Escalated",
              },
              {
                name: "Alice White",
                trigger: "Offshore transfer",
                risk: "Medium",
                status: "Under Review",
              },
              {
                name: "Olivia Henry  ",
                trigger: "Crypto funding",
                risk: "High",
                status: "Open Case",
              },
            ].map((row, i) => (
              <tr key={i} className="border-b hover:bg-gray-50 transition">
                <td className="p-2 font-medium text-gray-800">{row.name}</td>
                <td className="p-2 text-gray-600">{row.trigger}</td>

                <td className="p-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${
                      row.risk === "High"
                        ? "bg-red-100 text-red-700 border border-red-200"
                        : "bg-amber-100 text-amber-700 border border-amber-200"
                    }`}
                  >
                    {row.risk}
                  </span>
                </td>

                <td className="p-2">
                  <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-700 border">
                    {row.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div> */}

      {/* HEATMAP */}
      <div className="bg-white p-5 rounded-2xl border shadow-sm hover:shadow-md transition space-y-4">
        {/* HEADER */}
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-800">
            🔥 Transaction Activity Overview
          </h3>
        </div>

        {/* HEATMAP GRID */}
        <div className="grid grid-cols-12 gap-1">
          {heatmapDataCheck.slice(0, 72).map((item, i) => {
            const risk = item?.risk_status;

            const riskStyles = {
              high: 'bg-red-500 hover:bg-red-600',
              medium: 'bg-amber-400 hover:bg-amber-500',
              low: 'bg-emerald-500 hover:bg-emerald-600',
              default: 'bg-slate-200',
            };

            return (
              <div key={i} className="relative group">
                {/* Cell */}
                <div
                  className={`h-4 w-full rounded-md cursor-pointer transition-all duration-200 
          ${riskStyles[risk] || riskStyles.default}
          hover:scale-125 hover:shadow-lg`}
                />

                {/* Tooltip */}
                <div
                  className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 
                        hidden group-hover:block z-50"
                >
                  <div className="bg-white border border-slate-200 shadow-xl rounded-xl px-3 py-2 min-w-[190px]">
                    {/* Risk Badge */}
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-500">
                        Risk Level
                      </span>

                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-semibold
                ${
                  risk === 'high'
                    ? 'bg-red-100 text-red-700'
                    : risk === 'medium'
                      ? 'bg-amber-100 text-amber-700'
                      : risk === 'low'
                        ? 'bg-emerald-100 text-emerald-700'
                        : 'bg-slate-100 text-slate-500'
                }`}
                      >
                        {risk
                          ? risk.charAt(0).toUpperCase() + risk.slice(1)
                          : 'No Data'}
                      </span>
                    </div>

                    {/* Details */}
                    <div className="text-xs text-slate-600 space-y-1">
                      <div>
                        📅{' '}
                        <span className="font-medium">
                          {item?.date || 'No date'}
                        </span>
                      </div>
                      <div>
                        👤{' '}
                        <span className="font-medium">
                          {item?.customer?.name || 'Unknown Customer'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Arrow */}
                  <div className="w-2 h-2 bg-white border-l border-b border-slate-200 rotate-45 mx-auto -mt-1"></div>
                </div>
              </div>
            );
          })}
        </div>

        {/* LEGEND */}
        <div className="flex items-center justify-between text-xs mt-2">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm bg-emerald-200"></span>
              <span className="text-gray-600">Low</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm bg-amber-300"></span>
              <span className="text-gray-600">Medium</span>
            </div>

            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm bg-red-400"></span>
              <span className="text-gray-600">High</span>
            </div>
          </div>
        </div>

        {/* DESCRIPTION */}
        <div className="bg-gray-50 border rounded-lg p-3">
          <p className="text-[11px] text-gray-600 leading-relaxed">
            This info represents transaction activity over time. Darker or
            warmer colors indicate higher volumes or potentially suspicious
            patterns. Areas with repeated high risk may require further
            investigation.
          </p>
        </div>
      </div>
    </div>
  );
};

export default OngoingMonitoringModule;
